'use client';

import { useEffect, useState, useCallback } from 'react';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { workspaceApi } from '@/lib/api/workspace/workspaceApi';
import { toast } from 'sonner';
import type { Tag } from 'dico-types';
import { PickListSection } from '@/components/settings/PickListSection';

interface TagsTabContentProps {
  workspaceId: string;
}

export function TagsTabContent({ workspaceId }: TagsTabContentProps) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded'>('idle');

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const tagsRes = await workspaceApi.getWorkspaceTags(workspaceId);
      setTags(tagsRes.data ?? []);
      setLoadState('loaded');
    } catch {
      toast.error('Failed to load tags');
      setLoadState('loaded');
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadState === 'loading') {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <MonoIcon type="Loader2" className="text-muted-foreground animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-3xl mx-auto min-h-full flex-1 space-y-10 px-8 py-10 xl:border-x">
      <PickListSection
        title="Tags"
        description="Tags you can assign to tickets (e.g. Bug, Feature)."
        items={tags}
        showIcon
        defaultNewItemName="New label"
        onAdd={async (payload) => {
          const res = await workspaceApi.createTag(workspaceId, payload);
          if (res.success && res.data) {
            setTags((prev) => [...prev, res.data!]);
            toast.success('Label added');
            return res.data.id;
          }
          toast.error(res.error ?? 'Failed to add label');
          return null;
        }}
        onUpdate={async (id, payload) => {
          const res = await workspaceApi.updateTag(workspaceId, id, payload);
          if (res.success && res.data) {
            setTags((prev) => prev.map((t) => (t.id === id ? res.data! : t)));
            toast.success('Label updated');
          } else {
            toast.error(res.error ?? 'Failed to update label');
          }
        }}
        onDelete={async (id) => {
          const res = await workspaceApi.deleteTag(workspaceId, id);
          if (res.success) {
            setTags((prev) => prev.filter((t) => t.id !== id));
            toast.success('Label removed');
          } else {
            toast.error(res.error ?? 'Failed to remove label');
          }
        }}
      />
    </div>
  );
}
